<script setup lang="ts">
import { ref, computed } from 'vue'
import SearchBar from './SearchBar.vue'
import type { Group, Store } from '@/data'
import { RemoveFilter } from '@/components/icons'

interface IProps {
  label: string
  icon?: any
  data?: Group[] | Store[]
  placeholder?: string
  selectedCustomer?: string
  enableFilter?: boolean
}

const props = defineProps<IProps>()
const searchValue = ref<string>('')
// const filteredGroupId = ref<string>('')

const emit = defineEmits(['loadData', 'filterData', 'clearFilter'])

const handleChildClick = (child: Group | Store) => {
  if ('groupId' in child && !('storeId' in child)) {
    emit('loadData', 'group', child.groupId)
    if (child.groupId) {
      // filteredGroupId.value = child.groupId
      emit('filterData', 'group', child.groupId)
    }
  } 
  // else if ('storeId' in child) {
  //   emit('loadData', 'store', child.storeId)
  // }
}

const clearFilter = () => {
  emit('clearFilter')
  // filteredGroupId.value = ''
}

// Computed property to filter items
const searchResults = computed(() => {
  const dataArray = props.data ?? [] // Ensure it's always an array

  if (!searchValue.value) {
    return [...dataArray].sort((a, b) => a.name.localeCompare(b.name))
  }

  return dataArray
    .filter((item) => item.name.toLowerCase().includes(searchValue.value.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name))
})
</script>

<template>
  <div class="selection-label-container">
    <div class="label">
      <div class="left bold">
        <component class="icon" :is="props.icon" />
        <span>{{ props.label }}</span>
      </div>
      <div v-if="enableFilter 
      // && filteredGroupId !== ''
      " class="filter-button" @click="clearFilter">
        <RemoveFilter />
      </div>
    </div>
    <div class="selection-items-container">
      <SearchBar v-model:search="searchValue" />
      <div class="selection-items">
        <span
          class="item"
          :class="{ selected: enableFilter && item.name === selectedCustomer }"
          v-for="(item, index) in searchResults"
          :key="index"
          @click="handleChildClick(item)"
          >{{ item.name }}</span
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
.selection-label-container {
  position: relative;
  width: 100%;
  margin-bottom: 1.25rem;
}

.selection-label-container .label {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  user-select: none;
  padding: 0 0.9375rem 0 0;
  box-sizing: border-box;
  color: var(--color-text);
  transition: all 0.3s ease;
  height: 3.125rem;
}

.selection-label-container .label > div {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.selection-label-container .label .left {
  font-size: var(--font-size-body);
}

.selection-label-container .label .left .icon {
  font-size: var(--font-size-menu-icon);
}

.selection-label-container .label .left.bold {
  font-weight: 600;
  font-size: var(--font-size-h1);
}

.selection-label-container .selection-items-container {
  width: 100%;
  height: 12.5rem;
  transition: background-color 0.5s ease;
  background-color: var(--color-background);
  overflow: hidden;
  border-radius: 0.9375rem;
  padding: 0.3125rem 0.625rem;
  padding-bottom: 1.875rem;
}

.selection-label-container .selection-items-container .selection-items {
  padding-block: 0.625rem;
  padding-left: 0.9375rem;
  font-size: var(--font-size-small);
  cursor: pointer;
  height: 100%;
  overflow-y: scroll;
}

.selection-label-container .selection-items-container .selection-items::-webkit-scrollbar {
  display: none;
}

.selection-label-container .selection-items-container .selection-items .item {
  display: block;
  border-bottom: 0.5px solid var(--color-text-95);
  margin-bottom: 0.625rem;
  width: fit-content;
}

.selection-label-container .selection-items-container .selection-items .selected {
  color: var(--vt-c-tertiary);
  font-weight: 600;
}

.filter-button {
  cursor: pointer;
}
</style>
